import { createAction } from '@reduxjs/toolkit'
import { isUndefined } from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { prepareActiveDrawableBeforeSubmit } from '../utils/prepareActiveDrawableBeforeSubmit'
import {
    fetchOpeningGroupPending,
    filterDrawableGroups,
    setActiveDrawable,
    setActiveDrawableGroup,
    updateOpeningGroupsSuccess,
} from '../../../actions/drawable'
import { UpdateOpeningGroupApiResponse } from '../../../api/api-helper'
import { updateOpeningGroup } from '../../../api/takeoff-api'
import { OpeningGroup } from '../../../models/activeDrawable'
import { Project } from '../../../models/project'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { updateDrawableSelections } from '../../../shared/services/opening-form-services'
import { getUnitOfMeasureByDrawingType } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Workspace } from '../../lib/toolBoxes/2D'
import Select from '../../lib/toolBoxes/2D/tools/select/Select.tool'
import { resetNewCreatedDrawables, setFlag, unsetActiveDrawableId } from '../../slices/2D'
import { updateToolbarMessage } from '../../slices/common'
import { formError } from '../../slices/forms'
import { selectProject } from '../../slices/project'
import { selectAllRegions } from '../../slices/region'
import { activateTool } from '../../slices/tools'
import { DRAW_TOOL_ACTION, SAVING_MESSAGE } from '../../types'

export const submitMaterialForm = createAction<{ formData: OpeningGroup['settings'] }>('submitMaterialForm')

export function* handleMaterialFormSubmit({ payload }: ReturnType<typeof submitMaterialForm>) {
    try {
        yield put(updateToolbarMessage(SAVING_MESSAGE))

        const results = yield call(prepareActiveDrawableBeforeSubmit, { formData: payload.formData })
        const regions = yield select(selectAllRegions)

        // Something went wrong with preparing data for submit bail
        if (!results) return

        const {
            activeDrawableGroup,
            prepareDrawables,
            openings,
            settings,
            configuration,
            remarks,
            scaleFactor,
            dpi,
            xCalibrationFactor,
            yCalibrationFactor,
            pdfScale,
        } = results

        yield put(fetchOpeningGroupPending())

        const project: Project = yield select(selectProject)

        let preparedOpenings = yield call(updateDrawableSelections, openings, {
            settings,
            configuration,
            remarks,
        })

        if (!isUndefined(prepareDrawables)) {
            let drawables = preparedOpenings
            let nonHardwareDrawables: typeof preparedOpenings = []

            if (project?.is3D) {
                const partitionedOpenings = preparedOpenings.reduce(
                    (drawables, opening) => {
                        opening.type === DRAWABLE_TYPES.HARDWARE
                            ? drawables.hardware.push(opening)
                            : drawables.nonHardware.push(opening)

                        return drawables
                    },
                    { hardware: [], nonHardware: [] }
                )

                drawables = partitionedOpenings.hardware
                nonHardwareDrawables = partitionedOpenings.nonHardware
            }

            preparedOpenings = yield call(prepareDrawables, {
                drawables,
                newSettings: settings,
                scaleFactor,
                dpi,
                xCalibrationFactor,
                yCalibrationFactor,
                pdfScale,
                regions,
            })

            if (project?.is3D) {
                preparedOpenings = preparedOpenings.concat(nonHardwareDrawables)
            }
        }

        const req = {
            openings: preparedOpenings,
            settings: {
                ...settings,
                unit_of_measure: getUnitOfMeasureByDrawingType(settings.shape_type),
                bundle_name: activeDrawableGroup.type === DRAWABLE_TYPES.BEAM ? null : settings.bundle_name,
            },
            type: activeDrawableGroup.type,
            configuration,
            remarks,
            isSave: true,
        }
        const res = yield call(updateOpeningGroup, activeDrawableGroup.project_id, req, String(activeDrawableGroup.id))
        const formattedResponse: UpdateOpeningGroupApiResponse = {
            newGroup: res,
            originalGroup: {
                id: activeDrawableGroup.id,
                project_id: activeDrawableGroup.project_id,
                color: null,
                ...req,
            },
        }

        yield put(updateOpeningGroupsSuccess(formattedResponse))

        yield all([
            put({ type: activateTool.type, payload: Select.NAME }),
            put(unsetActiveDrawableId()),
            put(setActiveDrawable(null, 0)),
            put(setActiveDrawableGroup(null)),
            put(filterDrawableGroups()),
            put(setFlag(DRAW_TOOL_ACTION.NEW_OPENING)),
            put(resetNewCreatedDrawables()),
            put(updateToolbarMessage(null)),
        ])

        if (!project.is3D) {
            const manager: PaperManager | null = yield call(managers.get2DManager)

            if (!manager) return

            const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)

            yield call(workspaceTool.lockItems, false)
        }
    } catch (error) {
        yield put(formError((error as any).message))
    }
}

export function* watchForMaterialFormSubmit() {
    yield takeLatest(submitMaterialForm.type, handleMaterialFormSubmit)
}
